<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, provide, ref, useId, watch } from 'vue';
import NxDropdown, { type NxDropdownProps } from './NxDropdown.vue';
import { dropdownListInjectionKey } from './internal';

const props = defineProps<NxDropdownProps>();

const menuId = `dropdown-menu-${useId()}`;
const isOpen = ref(props.isOpen);
const triggerRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);

watch(
  () => props.isOpen,
  (newOpen) => {
    isOpen.value = newOpen;
  }
);

const emit = defineEmits<{ close: [] }>();

/**
 * The single close path. Every route out of the open state runs through here —
 * Escape, Tab, focus-out, an item's injected `close()`, the outside click — so a
 * controlled consumer (`:is-open` bound to its own ref) is always told. Emitting
 * from only one of them left such a consumer stuck at `true`, and its next
 * trigger click merely set it back to `false`: the menu needed two clicks to
 * reopen. Idempotent on purpose, so double calls do not emit twice.
 */
function closeList() {
  if (!isOpen.value) return;
  isOpen.value = false;
  emit('close');
}

provide(dropdownListInjectionKey, { close: closeList });

function getMenuItems(): HTMLElement[] {
  return Array.from(menuRef.value?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
}

function focusOption(index: number) {
  const items = getMenuItems();
  items[(index + items.length) % items.length]?.focus();
}

async function openAndFocus(target: 'first' | 'last') {
  isOpen.value = true;
  await nextTick();
  focusOption(target === 'first' ? 0 : -1);
}

function closeAndFocusTrigger() {
  closeList();
  triggerRef.value?.focus();
}

function onTriggerKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    openAndFocus('first');
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    openAndFocus('last');
  } else if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    if (isOpen.value) closeAndFocusTrigger();
    else openAndFocus('first');
  } else if (e.key === 'Escape' && isOpen.value) {
    e.preventDefault();
    closeList();
  }
}

function onTriggerClick() {
  if (isOpen.value) closeAndFocusTrigger();
  else openAndFocus('first');
}

function onMenuKeydown(e: KeyboardEvent) {
  const items = getMenuItems();
  if (e.key === 'Escape') {
    e.preventDefault();
    closeAndFocusTrigger();
    return;
  }
  if (e.key === 'Tab') {
    closeList();
    return;
  }
  if (items.length === 0) return;
  const current = items.indexOf(document.activeElement as HTMLElement);
  if (e.key === 'ArrowDown') {
    focusOption(current + 1);
  } else if (e.key === 'ArrowUp') {
    focusOption(current - 1);
  } else if (e.key === 'Home') {
    focusOption(0);
  } else if (e.key === 'End') {
    focusOption(-1);
  } else {
    return;
  }
  e.preventDefault();
}

function onFocusOut(e: FocusEvent) {
  const next = e.relatedTarget as Node | null;
  if (next && (triggerRef.value?.contains(next) || menuRef.value?.contains(next))) return;
  closeList();
}

/**
 * Outside-click dismiss. NxDropdown itself stays out of this (its content picks
 * options on mousedown, so a generic closer there would swallow the selection),
 * but the menu list owns `isOpen`, so it can dismiss safely: a pointerdown inside
 * the trigger or the menu is ignored, everything else closes. The menu keeps its
 * DOM position under `popover`, so `contains` still resolves.
 */
function onDocumentPointerDown(e: Event) {
  const target = e.target as Node | null;
  if (!target) return;
  if (triggerRef.value?.contains(target) || menuRef.value?.contains(target)) return;
  closeList();
}

// `immediate`: a consumer can render this list already open (`:is-open="true"`, or
// backing state that is true at mount). Without it no listener was attached at all,
// so the outside-click dismiss silently did nothing until the first toggle.
watch(
  isOpen,
  (open) => {
    if (open) document.addEventListener('pointerdown', onDocumentPointerDown, true);
    else document.removeEventListener('pointerdown', onDocumentPointerDown, true);
  },
  { immediate: true }
);

onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocumentPointerDown, true));

const triggerProps = computed(() => ({
  triggerRef,
  onTriggerKeydown,
  onTriggerClick,
  'aria-haspopup': 'menu' as const,
  'aria-expanded': isOpen.value,
  'aria-controls': menuId,
}));
</script>

<template>
  <div ref="wrapperRef" class="dropdown-root" @focusout="onFocusOut">
    <NxDropdown
      :is-open="isOpen"
      :align="props.align"
      :fit-width="props.fitWidth"
      @close="closeList"
    >
      <template #trigger>
        <slot name="trigger" v-bind="triggerProps">
          <button
            ref="triggerRef"
            type="button"
            aria-haspopup="menu"
            :aria-expanded="isOpen"
            :aria-controls="menuId"
            @keydown="onTriggerKeydown"
          />
        </slot>
      </template>
      <div :id="menuId" ref="menuRef" role="menu" class="dropdown-content" @keydown="onMenuKeydown">
        <slot />
      </div>
    </NxDropdown>
  </div>
</template>

<style scoped>
.dropdown-root {
  display: contents;
}

button {
  font-family: var(--nxt-font);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg);
  background-color: var(--nxt-surface-bg);
  display: flex;
  align-items: center;

  border: none;
  font-size: var(--nxt-font-size-xs); /* xs */
  padding: calc(0.275em / 0.75) var(--nxt-space-md);
  border: var(--nxt-border) var(--nxt-hairline) solid; /* 1px */
  cursor: pointer;
  gap: 0.125em; /* 2px */

  &:hover {
    border: var(--nxt-border-strong) var(--nxt-hairline) solid; /* 1px */
  }
}

.chevron-icon {
  transition: transform var(--nxt-motion-fast) ease;
  transform-origin: center;
}

.chevron-icon.down {
  transform: rotate(-180deg);
}

.dropdown-content {
  min-width: var(--nxt-panel-width-xs);
  background-color: var(--nxt-surface-raised-bg);
  border-radius: var(--nxt-radius-sm);
  padding: var(--nxt-space-2xs);
  box-shadow: var(--nxt-shadow-md);
}
</style>
